import { useState } from "react";
import { Link } from "react-router-dom";
import LandingNav from "@/pages/home/components/LandingNav";

const categories = [
  {
    icon: "ri-rocket-line",
    title: "Getting Started",
    questions: [
      { q: "How do I create my Pixory AI account?", a: "Visit the registration page, enter your studio name and email address, and follow the verification link sent to your inbox. Once verified, you can set your password and start exploring the platform immediately with our free plan." },
      { q: "Can I import existing client data?", a: "Yes. Pixory AI supports CSV import for client lists, including names, emails, phone numbers, and event dates. Navigate to Admin → Clients → Import to upload your file. Our system maps columns automatically and validates data before import." },
      { q: "Is there a setup guide for new studios?", a: "We recommend starting with: 1) Brand your studio under Settings → Branding, 2) Add your first client, 3) Create a contract template, 4) Explore the job workflow. Our onboarding checklist in the dashboard guides you through each step." },
    ],
  },
  {
    icon: "ri-file-text-line",
    title: "Contracts &amp; Payments",
    questions: [
      { q: "How do digital contracts work?", a: "Create a contract template with your standard terms, then generate a contract for any client. The client receives an email with a secure link to review and sign digitally. Signatures are legally binding and timestamped. You can track status in real time." },
      { q: "What payment methods are supported?", a: "Pixory AI supports manual payment tracking (cash, Zelle, bank transfer) and integrated payments via Stripe for credit and debit cards. Each payment is logged with method, amount, date, and associated contract or job for complete financial tracking." },
      { q: "Can clients pay through their portal?", a: "Yes. When Stripe integration is enabled, clients can pay invoices directly through their client portal. Payment status updates automatically in your admin dashboard. You can also set up deposit requirements and payment schedules." },
    ],
  },
  {
    icon: "ri-camera-line",
    title: "Jobs &amp; Workflow",
    questions: [
      { q: "How do I create and manage photo jobs?", a: "From Admin → Jobs, click New Job and select the client, event type, date, and assign team members. Each job has a customizable workflow with steps like Consultation, Session, Editing, Selection, and Delivery. Move tasks through stages as work progresses." },
      { q: "Can clients select their favorite photos?", a: "Absolutely. Upload photos to a job and enable the Photo Selection feature. Clients access their portal to mark favorites, leave comments on specific images, and submit their selections. You receive notifications when selections are complete." },
      { q: "How do deliveries work?", a: "Create delivery packages with selected or edited photos. Set download limits, expiration dates, and optional password protection. Clients receive a secure link to download their images. You can track who downloaded what and when." },
    ],
  },
  {
    icon: "ri-mail-send-line",
    title: "Invitations &amp; Portals",
    questions: [
      { q: "What are digital invitations?", a: "Digital invitations are beautiful, customizable event pages you can send to clients for weddings, quinceañeras, corporate events, and more. Each invitation includes event details, RSVP collection, photo galleries, and music. No paper, no printing — fully digital." },
      { q: "How does the client portal work?", a: "Each client gets a secure, private portal where they can view their contracts, make payments, track job progress, select photos, download deliveries, and message you. The portal is accessible via a unique token-based URL — no client login required." },
      { q: "Can I customize invitation designs?", a: "Yes. Pixory AI includes a template library with designs for different event types. You can customize colors, fonts, images, music, and content. Custom CSS is available on higher-tier plans for complete design control." },
    ],
  },
  {
    icon: "ri-shield-check-line",
    title: "Account &amp; Security",
    questions: [
      { q: "How do I reset my password?", a: "On the login page, click Forgot Password and enter your email. You will receive a secure reset link valid for one hour. For security, password reset links can only be used once and expire after use." },
      { q: "Is my data backed up?", a: "Yes. All data is automatically backed up daily with point-in-time recovery. Encrypted backups are stored across multiple geographic regions. You can also export your data at any time from Settings → Data Export." },
      { q: "How do I manage team access?", a: "Depending on your plan, you can add team members from Settings → Team. Assign roles with granular permissions — photographers can manage jobs while accountants access only payments and contracts. All actions are logged for audit purposes." },
    ],
  },
];

const faqs = [
  { q: "What is Pixory AI?", a: "Pixory AI is a complete business management platform designed specifically for photography studios. It handles clients, contracts, payments, job workflows, digital invitations, photo delivery, and client portals — all in one place." },
  { q: "Is there a free plan?", a: "Yes. The Free plan includes 1 user, up to 50 clients, 10 active jobs, and basic branding. It is fully functional with no time limit — perfect for solo photographers getting started." },
  { q: "Can I upgrade or downgrade my plan?", a: "You can change plans at any time from Settings → Billing. Upgrades take effect immediately with prorated charges. Downgrades apply at the start of your next billing cycle. No long-term contracts required." },
  { q: "Do you offer phone support?", a: "We provide email support with a typical response time under 4 hours during business days. Priority support with faster response times is included in Pro and Enterprise plans. Live chat support is coming soon." },
  { q: "Can I cancel anytime?", a: "Absolutely. Cancel from your account settings at any time. Your data is retained for 30 days after cancellation, giving you time to export everything. No cancellation fees, no questions asked." },
];

export default function HelpCenterPage() {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const filteredCategories = categories.map((cat) => ({
    ...cat,
    questions: cat.questions.filter(
      (item) =>
        item.q.toLowerCase().includes(search.toLowerCase()) ||
        item.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => cat.questions.length > 0);

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white font-sans">
      <LandingNav />

      <section className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">Support</p>
            <h1 className="text-4xl md:text-5xl font-black mb-4">Help Center</h1>
            <p className="text-white/40 text-base max-w-lg mx-auto mb-8">
              Find answers, explore guides, and learn how to get the most out of Pixory AI for your photography studio.
            </p>
            <div className="relative max-w-md mx-auto">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-white/30">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search help articles..."
                className="w-full bg-[#141418] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-[#C8A96E]/40 transition-colors"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-3 mb-16">
            {filteredCategories.map((cat) => (
              <div key={cat.title} className="bg-[#141418] rounded-2xl border border-white/5 overflow-hidden">
                <button
                  onClick={() => setOpenCategory(openCategory === cat.title ? null : cat.title)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 text-[#C8A96E]">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="font-semibold text-sm">{cat.title}</span>
                    <span className="text-white/20 text-xs">{cat.questions.length} articles</span>
                  </div>
                  <span className="w-5 h-5 flex items-center justify-center text-white/30 transition-transform" style={{ transform: openCategory === cat.title ? "rotate(180deg)" : "rotate(0deg)" }}>
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </span>
                </button>
                {openCategory === cat.title && (
                  <div className="px-5 pb-5 space-y-1">
                    {cat.questions.map((item, idx) => (
                      <div key={idx} className="pl-12">
                        <button
                          onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                          className="w-full text-left py-2.5 text-sm text-white/60 hover:text-white transition-colors cursor-pointer flex items-center justify-between"
                        >
                          {item.q}
                          <span className="w-4 h-4 flex items-center justify-center text-white/20">
                            <i className={`text-xs ${openFaq === idx ? "ri-subtract-line" : "ri-add-line"}`}></i>
                          </span>
                        </button>
                        {openFaq === idx && (
                          <p className="text-white/40 text-xs leading-relaxed pb-3">{item.a}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* General FAQ */}
          <div>
            <h2 className="text-2xl font-black mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-[#141418] rounded-xl border border-white/5 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx + 100 ? null : idx + 100)}
                    className="w-full text-left p-4 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors"
                  >
                    <span className="text-sm font-medium pr-4">{faq.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center text-white/30 shrink-0">
                      <i className={`text-sm ${openFaq === idx + 100 ? "ri-subtract-line" : "ri-add-line"}`}></i>
                    </span>
                  </button>
                  {openFaq === idx + 100 && (
                    <div className="px-4 pb-4">
                      <p className="text-white/50 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-[#141418] rounded-2xl border border-white/5 p-8 text-center">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#C8A96E] mx-auto mb-4">
              <i className="ri-question-answer-line text-lg"></i>
            </span>
            <h3 className="text-white font-bold text-lg mb-2">Still need help?</h3>
            <p className="text-white/40 text-sm mb-5 max-w-md mx-auto">
              Our support team is ready to assist you. Typical response time is under 4 hours during business days.
            </p>
            <a
              href="mailto:support@pixory.ai"
              className="inline-flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-6 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Support
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-white/30 hover:text-white/60 text-xs transition-colors">← Back to Pixory AI</Link>
          <span className="text-white/20 text-xs">© 2025 Pixory AI. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}